import { Box, CircularProgress } from '@mui/material';
import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { useCreateBenchmarkOverwriteMutation, useDeleteBenchmarkOverwriteMutation, useGetBenchmarkChartDataQuery, useGetBenchmarkingQuery, useGetBenchmarkOverwriteQuery, useGetBuildingUseListQuery, useGetHeatInstallationListQuery, useGetMonthlyDataQuery, useGetPortfolioAnalysisQuery, useGetPortfolioDurationAnalysisQuery, useGetPortfolioHourAnalysisQuery, usePostPortfolioUpdateNowMutation, useSetDaytimeIntervalMutation, useSetDaytimeIntervalUnitMutation, useUpdateBenchmarkOverwriteMutation } from "../../../api/energy-analysis/portfolioAnalysisApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedAccount } from "../../../redux/selectedAccountSlice";

import { showSnackbar } from "../../../redux/snackbarSlice";
import { DateTime } from 'luxon';
import { useGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';

const MeterDrawer = React.lazy(() =>
  import('remote/MeterDrawer').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

import { downLoadCsvFile } from 'remote/utils/export';

const EditMatrikelDataDialog = React.lazy(() => import('remote/EditMatrikelDataDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const DurationAnalysisDialog = React.lazy(() => import('remote/DurationAnalysisDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const HourAnalysisDialog = React.lazy(() => import('remote/HourAnalysisDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const PortfolioAnalysisGroupedColumnChart = React.lazy(() => import('remote/PortfolioAnalysisGroupedColumnChart').catch(() => ({ default: () => <div>Failed to load</div> })));
const PortfolioAnalysisDataChart = React.lazy(() => import('remote/PortfolioAnalysisDataChart').catch(() => ({ default: () => <div>Failed to load</div> })));
const EditRowDataDialog = React.lazy(() => import('remote/EditRowDataDialog').catch(() => ({ default: () => <div>Failed to load</div> })));

const MeterContainer = ({ isOpen, onClose, meter, meterType, analysisType }) => {

  const dispatch = useDispatch()


  if (meter?.data_stream === undefined) return null;
  const account = useSelector(getSelectedAccount)
  const [selectedDateRange, setSelectedDateRange] = useState({ start: DateTime.now().minus({ weeks: 4 }).startOf('day').toMillis(), end: DateTime.now().startOf('day').toMillis() });


  // Edit matrikel data feature
  const [selectedMatrikel, setSelectedMatrikel] = useState({});

  const { data: benchmarkOverwriteData, isError: isBenchmarkOverwriteError, isLoading: isBenchmarkOverwriteLoading } = useGetBenchmarkOverwriteQuery({ account_id: account?.AccountId }, { skip: !account?.AccountId });

  const [deleteBenchmarkOverwrite] = useDeleteBenchmarkOverwriteMutation();
  const [updateBenchmarkOverwrite] = useUpdateBenchmarkOverwriteMutation();
  const [createBenchmarkOverwrite] = useCreateBenchmarkOverwriteMutation();
  const [updatePortfolioAnalysis] = usePostPortfolioUpdateNowMutation();

  const { data: buildingUseList, isError: isBuildingUseListError, isLoading: isBuildingUseListLoading } = useGetBuildingUseListQuery();
  const { data: heatInstallationList, isError: isHeatInstallationListError, isLoading: isHeatInstallationListLoading } = useGetHeatInstallationListQuery();

  const handleSaveChanges = async (data) => {
    const existingEntry = benchmarkOverwriteData?.find(o => o.MatrikelKey == selectedMatrikel.matrikel_key);

    let response = null;
    if (existingEntry) {
      response = await updateBenchmarkOverwrite({
        account_id: account?.AccountId, matrikel_key: selectedMatrikel.matrikel_key, body:
        {
          SumBuildingArea: !data?.SumBuildingArea || data?.SumBuildingArea === selectedMatrikel?.SumBuildingArea ? null : parseInt(data?.SumBuildingArea),
          AverageBuildingYear: !data?.AverageBuildingYear || data?.AverageBuildingYear === selectedMatrikel?.AverageBuildingYear ? null : parseInt(data?.AverageBuildingYear),
          ExtractedBuildingUse: !data?.ExtractedBuildingUse || data?.ExtractedBuildingUse === selectedMatrikel?.ExtractedBuildingUse ? null : data?.ExtractedBuildingUse,
          ExtractedHeatInstallation: !data?.ExtractedHeatInstallation || data?.ExtractedHeatInstallation === selectedMatrikel?.ExtractedHeatInstallation ? null : data?.ExtractedHeatInstallation,
          VerificationFlag: !data?.VerificationFlag || data?.VerificationFlag === selectedMatrikel?.VerificationFlag ? false : data?.VerificationFlag
        }
      });
    } else {
      response = await createBenchmarkOverwrite({
        account_id: account?.AccountId, body: {
          MatrikelKey: selectedMatrikel.matrikel_key,
          SumBuildingArea: !data?.SumBuildingArea || data?.SumBuildingArea === selectedMatrikel?.SumBuildingArea ? null : parseInt(data?.SumBuildingArea),
          AverageBuildingYear: !data?.AverageBuildingYear || data?.AverageBuildingYear === selectedMatrikel?.AverageBuildingYear ? null : parseInt(data?.AverageBuildingYear),
          ExtractedBuildingUse: !data?.ExtractedBuildingUse || data?.ExtractedBuildingUse === selectedMatrikel?.ExtractedBuildingUse ? null : data?.ExtractedBuildingUse,
          ExtractedHeatInstallation: !data?.ExtractedHeatInstallation || data?.ExtractedHeatInstallation === selectedMatrikel?.ExtractedHeatInstallation ? null : data?.ExtractedHeatInstallation,
          VerificationFlag: !data?.VerificationFlag || data?.VerificationFlag === selectedMatrikel?.VerificationFlag ? false : data?.VerificationFlag
        }
      });
    }

    if (response?.error?.status) {
      dispatch(showSnackbar({ message: 'Der opstod en fejl i opdateringen', severity: 'error' }));
      return;
    } else {
      dispatch(showSnackbar({ message: 'Ændringerne er gemt. Bemærk at der kan gå op til 1 time før ændringen tager effekt.', severity: 'success' }));
      updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
      setSelectedMatrikel({});
    }
  }

  const handleDelete = async () => {
    const response = await deleteBenchmarkOverwrite({ account_id: account?.AccountId, matrikel_key: selectedMatrikel.matrikel_key })

    if (response?.error?.status) {
      console.error('Error deleting overwrite data')
      dispatch(showSnackbar({ message: 'Der opstod en fejl i opdateringen', severity: 'error' }));
    } else {
      dispatch(showSnackbar({ message: 'Ændringerne er gemt. Bemærk at der kan gå op til 1 time før ændringen tager effekt.', severity: 'success' }));
      updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
      setSelectedMatrikel({});
    }
  }


  // Edit occupancy feature
    const [selectedRowSettings, setSelectedRowSettings] = useState({})
  
   const [setDaytimeInterval] = useSetDaytimeIntervalMutation();
   const [setDaytimeIntervalUnit] = useSetDaytimeIntervalUnitMutation();
  
    const handleSaveOccupancyChange = async (data) => {
      const { daytime_start, daytime_end } = data;

      let response;
      if (analysisType === 'power')
      {
        response = await setDaytimeInterval({
          account_id: account?.AccountId,
          metering_point_id: selectedRowSettings?.metering_point_id,
          daytime_start: daytime_start ?? null,
          daytime_end: daytime_end ?? null,
      });
      } else {
        response = await setDaytimeIntervalUnit({
          account_id: account?.AccountId,
          unit_id: JSON.parse(selectedRowSettings?.data_stream).unit_id,
          meter_type: analysisType,
          daytime_start: daytime_start ?? null,
          daytime_end: daytime_end ?? null,
      });
      }
  
      if (response?.error?.status) {
        console.error('Error updating row data:');
        dispatch(showSnackbar({ message: 'Der opstod en fejl i opdateringen', severity: 'error' }));
        return;
      } else {
        dispatch(showSnackbar({ message: 'Ændringerne er gemt. Bemærk at der kan gå op til 2 timer før ændringen tager effekt.', severity: 'success' }));
        updatePortfolioAnalysis({ recalc_account_id: account?.AccountId });
        setSelectedRowSettings({});
      }
    }


  // Charts
  const [selectedChart, setSelectedChart] = useState('consumption');


  // DurationAnalysisDialog feature

  const [durationAnalysisFilterOptions, setDurationAnalysisFilterOptions] = useState({})
  const { data: durationAnalysisData, isError: isDurationAnalysisError, isFetching: isDurationAnalysisFetching } = useGetPortfolioDurationAnalysisQuery({
    accountId: account?.AccountId,
    meteringPointId: meter?.metering_point_id,
    start: selectedDateRange?.start,
    end: selectedDateRange?.end,
    ...durationAnalysisFilterOptions,
  }, { skip: !meter?.metering_point_id || analysisType !== 'power' || meterType === 'matrikel'   });

  // Hour analysis feature

  const [hourAnalysisFilterOptions, setHourAnalysisFilterOptions] = useState({})
  const { data: hourAnalysisData, isError: isHourAnalysisError, isFetching: isHourAnalysisFetching } = useGetPortfolioHourAnalysisQuery({
    accountId: account?.AccountId,
    meteringPointId: meter?.metering_point_id,
    start: selectedDateRange?.start,
    end: selectedDateRange?.end,
    ...hourAnalysisFilterOptions,
  }, { skip: !meter?.metering_point_id || analysisType !== 'power' || meterType === 'matrikel' });

  //Groued column chart feature
  const { data: chartData, isError: isChartError, isLoading: isChartLoading, isFetching: isChartFetching } = useGetMonthlyDataQuery({ ...JSON.parse(meter?.data_stream) }, { skip: !meter?.metering_point_id  || analysisType !== 'power'   || meterType === 'matrikel' });


  //Consumption chart

  function formatAndEncodeTimestampForAPI(date) {
    const isoString = date.toISO();
    const response = encodeURIComponent(isoString);
    return response;
  }

  const stableQueryArgs = useMemo(() => {
    const data_stream = {...JSON.parse(meter?.data_stream)}
    return ({
    from: formatAndEncodeTimestampForAPI(DateTime.fromMillis(selectedDateRange.start).startOf('day')),
    to: formatAndEncodeTimestampForAPI(DateTime.fromMillis(selectedDateRange.end).endOf('day')),
    unitId: data_stream.unit_id,
    datatype: data_stream.data_type,
    unitTypeInputId: data_stream.unit_type_input_id,
    aggregateType: 'Hour',
  })}, [selectedDateRange, meter]);

  const { data: unitData, isError: isUnitError, isLoading: isUnitLoading, isFetching: isUnitFetching } = useGetDataQuery(stableQueryArgs, { skip: !meter?.data_stream });

  const chartProps = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return {
          open: !!meter?.metering_point_id,
          onClose: () => { },
          row: meter,
          data: durationAnalysisData,
          isLoading: isDurationAnalysisFetching,
          setFilterOptions: setDurationAnalysisFilterOptions
        };
      case 'hourAnalysis':
        return {
          open: !!meter?.metering_point_id,
          onClose: () => { },
          row: meter,
          data: hourAnalysisData,
          isLoading: isHourAnalysisFetching,
          setFilterOptions: setHourAnalysisFilterOptions
        };
      case 'consumption':
        return {
          address: meter?.address,
          data: unitData,
          isLoading: isUnitFetching,
          unit: analysisType === 'water' ? 'm³' : 'kWh'
        };
      case 'yearOnYear':
        return {
          address: meter?.address,
          data: chartData,
          isLoading: isChartFetching,
        };
      default:
        return {};
    }
  }, [selectedChart, hourAnalysisData, durationAnalysisData, meter, selectedDateRange, isDurationAnalysisFetching, isHourAnalysisFetching, isUnitFetching, isChartFetching, chartData, unitData]);

  const Chart = useMemo(() => {
    switch (selectedChart) {
      case 'durationAnalysis':
        return DurationAnalysisDialog;
      case 'hourAnalysis':
        return HourAnalysisDialog;
      case 'consumption':
        return PortfolioAnalysisDataChart;
      case 'yearOnYear':
        return PortfolioAnalysisGroupedColumnChart;
      default:
        return null;
    }
  }, [selectedChart]);

    useEffect(() => {
      if (isUnitError || isChartError || isHourAnalysisError || isDurationAnalysisError)
        dispatch(showSnackbar({ message: 'Der skete en uventet fejl ved hentning af data', severity: 'error' }));
    }, [isUnitError, isChartError, isHourAnalysisError, isDurationAnalysisError])

  return (<>
    <Suspense fallback={
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', verticalAlign: 'center' }}>
        <CircularProgress />
      </Box>
    }>
      <MeterDrawer meter={meter} meterType={meterType} open={isOpen} onClose={() => {
        onClose()
        setSelectedChart('consumption')
        }} setSelectedMatrikel={setSelectedMatrikel} setSelectedRowSettings={setSelectedRowSettings} Chart={Chart} chartProps={chartProps} setSelectedChart={setSelectedChart} selectedChart={selectedChart} setSelectedDateRange={setSelectedDateRange} selectedDateRange={selectedDateRange} downloadCsv={() => downLoadCsvFile(unitData, `${meter.address}_data.csv`)} analysisType={analysisType}>
      <Suspense fallback={<CircularProgress/>}>
        <EditMatrikelDataDialog
          open={!!selectedMatrikel?.matrikel_key}
          onClose={() => setSelectedMatrikel({})}
          address={selectedMatrikel?.address}
          initialState={(() => {
            const overwrite =
            benchmarkOverwriteData?.find(
              o => o.MatrikelKey === selectedMatrikel?.MatrikelKey
            ) ?? {};
          
          const finalMatrikel = {
            ...selectedMatrikel,                                         // start with the baseline
            ...Object.fromEntries(                                       // add only the useful overwrites
                Object.entries(overwrite).filter(([, v]) => v != null)   // skip null / undefined
            )
          };
          return finalMatrikel
        })()
        }
          overwriteData={benchmarkOverwriteData?.find(o => o.MatrikelKey === selectedMatrikel?.MatrikelKey)}
          buildingUseList={buildingUseList}
          heatInstallationList={heatInstallationList}
          saveChanges={handleSaveChanges}
          handleDelete={handleDelete}
        />
        </Suspense>
        <Suspense fallback={<CircularProgress/>}>
        <EditRowDataDialog
          open={!!selectedRowSettings?.data_stream}
          onClose={() => setSelectedRowSettings({})}
          row={selectedRowSettings}
          initialState={{ daytime_end: selectedRowSettings.daytime_end ?? 0, daytime_start: selectedRowSettings.daytime_start ?? 0 }}
          saveChanges={handleSaveOccupancyChange}
          address={selectedRowSettings?.address}

        />
        </Suspense>
      </MeterDrawer>
    </Suspense>
  </>);
};

export default MeterContainer;